/**
 * TanStack Query configuration. Spec 22.4's retry policy.
 *
 * "Queries (GET): 3 attempts, exponential backoff. Mutations: no automatic retry except on
 * network failure, and only because commandId makes it safe."
 */
import { QueryClient } from '@tanstack/react-query';

import { appErrorFrom, isRecoverable } from './errors';

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          const appError = appErrorFrom(error);
          /*
           * Retrying a 401 or a 403 is pointless — the answer will not change without the
           * user doing something — and it delays the redirect that would actually help.
           */
          if (appError !== null && !isRecoverable(appError.code)) return false;
          return failureCount < 3;
        },
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30_000),
        /* The board and profile do not change on their own; refetching on focus is noise. */
        refetchOnWindowFocus: false,
        staleTime: 30_000,
      },
      mutations: {
        /*
         * Spec 22.4: no automatic retry. Game commands carry a `commandId` that makes a
         * retry idempotent (spec 8.3), but nothing in this phase does, and a silently
         * repeated sign-up is worse than a visible failure.
         */
        retry: false,
      },
    },
  });
}
